import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api.js';
import { userFacingDetail } from '../utils/errorHandler.js';
import './FileUploadSection.css';

/**
 * 汎用ファイルアップロードセクション (D&D / 複数ファイル対応版)
 *
 * getAttachmentNotice は、登録済みファイルごとに出す注記を呼び出し側が決めるための
 * 差し込み口 (Issue #1057)。`(attachment) => ({badge, full}) | null` を渡すと、null 以外を
 * 返したファイルのカードに注記を出す。本コンポーネントは履歴書・面談音声・求人ファイルで
 * 共用するため、特定の用途の項目 (text_extracted 等) をここで解釈しない。
 *
 * replaceDisabledReason は、登録済みファイルの差し替え（「上書きする」）を止めるときの理由の文言
 * (Issue #1247、TU-D-024。面談音声の話者分離が処理中の間)。文字列を渡すと「上書きする」を押せなくし、
 * 近くにその文言を出す。null（既定）のときは今までどおり。
 */
function FileUploadSection({
    uploadUrlEndpoint,
    confirmEndpoint,
    getEndpoint,
    deleteEndpoint, // deleteEndpoint は /path とし、複数時 idが付加される想定
    label = 'ファイル',
    accept = 'application/pdf,text/plain,text/csv,.txt,.csv',
    maxFileSizeMB = 10,
    multiFile = false,
    maxFileCount = 1,
    maxTotalSizeMB = 10,
    onUploadComplete,
    onAttachmentChange,
    getAttachmentNotice,
    replaceDisabledReason = null,
}) {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const replaceReasonId = useId();

    const onAttachmentChangeRef = useRef(onAttachmentChange);
    onAttachmentChangeRef.current = onAttachmentChange;

    const [attachments, setAttachments] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);

    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    // D&D state
    const [isDragActive, setIsDragActive] = useState(false);

    // Delete modal
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Overwrite modal (Single file mode)
    const [overwriteConfirmVisible, setOverwriteConfirmVisible] = useState(false);

    // Toggle Dropzone Visibility
    const [showDropzone, setShowDropzone] = useState(false);

    const isFileTypeAllowed = (file) => {
        if (!accept || accept === '*') return true;
        const acceptedTypes = accept.split(',').map(t => t.trim().toLowerCase());
        const fileType = file.type.toLowerCase();
        const fileName = file.name.toLowerCase();
        
        return acceptedTypes.some(type => {
            if (type.startsWith('.')) return fileName.endsWith(type);
            if (type.endsWith('/*')) return fileType.startsWith(type.replace('/*', ''));
            return fileType === type;
        });
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const fetchDownloadUrl = async (id, disposition = 'attachment') => {
        const response = await api.fetch(`/attachments/${id}/download_url?disposition=${disposition}`);
        if (!response || !response.ok) throw new Error('ダウンロードURLの取得に失敗しました。');
        return response.json();
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.fetch(getEndpoint);
            if (response && response.ok) {
                const data = await response.json();
                if (multiFile) {
                    setAttachments(data.attachments || []);
                    setMeta(data.meta || null);
                    onAttachmentChangeRef.current?.(data.attachments || []);
                } else {
                    const att = data.attachment ? [data.attachment] : [];
                    setAttachments(att);
                    onAttachmentChangeRef.current?.(data.attachment);
                }
            } else {
                setAttachments([]);
                onAttachmentChangeRef.current?.(multiFile ? [] : null);
            }
        } catch {
            setAttachments([]);
            onAttachmentChangeRef.current?.(multiFile ? [] : null);
        } finally {
            setLoading(false);
        }
    }, [getEndpoint, multiFile]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFilesAdded = (filesArr) => {
        setError(null);
        if (filesArr.length === 0) return;

        let currentCount = attachments.length + selectedFiles.length;
        let currentSize = attachments.reduce((sum, att) => sum + (att.file_size || 0), 0) 
                          + selectedFiles.reduce((sum, f) => sum + f.size, 0);
        
        const validFiles = [];
        const errorMessages = [];
        let limitExceededCount = 0;

        // 全体を一括で弾くのではなく、通るものだけリストに入れる
        for (let i = 0; i < filesArr.length; i++) {
            const file = filesArr[i];
            const sizeMB = file.size / 1024 / 1024;

            // 1. ファイル形式チェック
            if (!isFileTypeAllowed(file)) {
                errorMessages.push(`「${file.name}」は未対応形式のため除外されました。`);
                continue;
            }

            // 2. 個別サイズチェック
            if (sizeMB > maxFileSizeMB) {
                errorMessages.push(`「${file.name}」はサイズ上限（${maxFileSizeMB}MB）を超過するため除外されました。`);
                continue;
            }

            // 3. 件数チェック
            if (multiFile) {
                if (currentCount + 1 > maxFileCount) {
                    limitExceededCount++;
                    continue;
                }
            } else {
                // 単一ファイルモードの場合、複数ドロップされたら先頭1件だけ採用
                if (validFiles.length >= 1) {
                    continue; 
                }
            }

            // 4. 合計サイズチェック (multiFileのみ)
            if (multiFile && meta) {
                if (currentSize + file.size > maxTotalSizeMB * 1024 * 1024) {
                    errorMessages.push(`「${file.name}」は合計サイズ上限を超えるため除外されました。`);
                    continue;
                }
            }

            // 全てのチェックを通過したら採用
            validFiles.push(file);
            currentCount++;
            currentSize += file.size;
        }

        if (limitExceededCount > 0) {
            errorMessages.push(`${limitExceededCount}件のファイルは最大ファイル数（${maxFileCount}件）の上限に達したため除外されました。`);
        }

        if (errorMessages.length > 0) {
            setError(errorMessages.join('\n'));
        }

        if (validFiles.length > 0) {
            if (multiFile) {
                setSelectedFiles(prev => [...prev, ...validFiles]);
            } else {
                setSelectedFiles([validFiles[0]]);
            }
        }
    };

    const handleFileChange = (event) => {
        const filesArr = Array.from(event.target.files);
        handleFilesAdded(filesArr);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragActive(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragActive(false);
        const filesArr = Array.from(e.dataTransfer.files);
        handleFilesAdded(filesArr);
    };

    const removeSelectedFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
        setError(null);
    };

    // アップロードのコアロジック (単一ファイル分)
    const uploadSingleFile = async (file) => {
        const contentType = file.type || 'application/pdf';
        const urlResponse = await api.fetch(`${uploadUrlEndpoint}?content_type=${encodeURIComponent(contentType)}`);
        if (!urlResponse || !urlResponse.ok) {
            const errorData = await urlResponse?.json().catch(() => null);
            throw new Error(userFacingDetail(urlResponse?.status, errorData) || '署名付きURLの取得に失敗しました。');
        }
        const { upload_url, blob_name } = await urlResponse.json();

        const uploadResponse = await fetch(upload_url, {
            method: 'PUT',
            headers: { 'Content-Type': contentType },
            body: file,
        });
        if (!uploadResponse.ok) throw new Error('アップロードに失敗しました。');

        const confirmResponse = await api.fetch(
            `${confirmEndpoint}?blob_name=${encodeURIComponent(blob_name)}&file_name=${encodeURIComponent(file.name)}`,
            { method: 'POST', headers: { 'Content-Type': 'application/json' } }
        );
        if (!confirmResponse || !confirmResponse.ok) {
            const errorData = await confirmResponse?.json().catch(() => null);
            throw new Error(userFacingDetail(confirmResponse?.status, errorData) || '確認に失敗しました。');
        }
        return await confirmResponse.json();
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) return;
        
        // 単一ファイルの上書き確認
        if (!multiFile && attachments.length > 0) {
            setOverwriteConfirmVisible(true);
            return;
        }

        await executeUpload();
    };

    const executeUpload = async () => {
        setOverwriteConfirmVisible(false);
        setError(null);
        setUploading(true);

        let successCount = 0;
        const uploadResults = [];
        const failedFiles = [];
        let lastErrorMessage = '';

        try {
            // 直列アップロード
            for (let i = 0; i < selectedFiles.length; i++) {
                try {
                    const result = await uploadSingleFile(selectedFiles[i]);
                    uploadResults.push(result);
                    successCount++;
                } catch (e) {
                    // 以降のファイルのアップロードは中断する
                    lastErrorMessage = e.message;
                    
                    // エラーになったファイル以降すべてを failedFiles に詰める
                    for (let j = i; j < selectedFiles.length; j++) {
                        failedFiles.push(selectedFiles[j]);
                    }
                    break;
                }
            }
        } finally {
            // エラーで中断された場合でも、成功した分はシームレスに画面構成に反映させる
            await fetchData();
            setUploading(false);
            
            // アップロード成功分があれば、後続のフック（文字起こし等）を発火させる
            if (successCount > 0 && typeof onUploadComplete === 'function') {
                onUploadComplete(uploadResults);
            }
            
            if (failedFiles.length === 0) {
                // 完全成功
                setSelectedFiles([]);
                setShowDropzone(false);
            } else {
                // 一部（または全件）失敗
                setSelectedFiles(failedFiles);
                setError(successCount > 0 
                    ? `一部のファイルアップロードが完了しましたが、エラーで中断されました: ${lastErrorMessage}` 
                    : `アップロードに失敗しました: ${lastErrorMessage}`
                );
            }
        }
    };

    const handleView = async (attachment) => {
        try {
            const data = await fetchDownloadUrl(attachment.id, 'inline');
            navigate('/file-viewer', {
                state: {
                    downloadUrl: data.download_url,
                    filename: data.file_name || attachment.file_name,
                    mimeType: attachment.file_type,
                    title: label,
                    subtitle: attachment.file_name,
                },
            });
        } catch (err) { setError(err.message); }
    };

    const handleDelete = async () => {
        if (!deleteTargetId) return;
        setDeleteLoading(true);
        try {
            const url = multiFile ? `${deleteEndpoint}/${deleteTargetId}` : deleteEndpoint;
            const response = await api.fetch(url, { method: 'DELETE' });
            if (!response || !response.ok) {
                const errorData = await response?.json().catch(() => null);
                throw new Error(userFacingDetail(response?.status, errorData) || '削除に失敗しました。');
            }
            await fetchData();
            setDeleteTargetId(null);
        } catch (err) { setError(err.message); }
        finally { setDeleteLoading(false); }
    };

    if (loading) {
        return (
            <div className="detail-item">
                <span className="detail-label">{label}</span>
                <div className="skeleton-line" style={{ height: '16px', width: '120px' }} />
            </div>
        );
    }

    const currentTotalSize = attachments.reduce((sum, att) => sum + (att.file_size || 0), 0);
    const selectedTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
    // 複数ファイルモード時は上限到達でボタンごと非表示。
    // 単一ファイルモード時は「上書き」が可能なため、上限ブロック（isAtLimit）は適用しない。
    const isAtLimit = multiFile ? attachments.length >= maxFileCount : false;
    // 登録済みファイルの差し替えを止める（Issue #1247、TU-D-024）。単一ファイルモードで、登録済みがあるときだけ効く。
    // 枠を開いたまま止まる状態になった場合も、差し替えの枠は出さない
    const replaceBlocked = !multiFile && attachments.length > 0 && Boolean(replaceDisabledReason);

    return (
        <>
            {uploading && (
                <div className="loading-popup-overlay">
                    <div className="loading-popup-content">
                        <div className="spinner"></div>
                        <span>アップロード中...</span>
                    </div>
                </div>
            )}

            {deleteTargetId && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3 className="modal-title">削除の確認</h3>
                        <p className="fus-delete-confirm-text">
                            ファイルを削除してもよろしいですか？
                        </p>
                        <div className="modal-actions">
                            <button className="modal-button-cancel" onClick={() => setDeleteTargetId(null)} disabled={deleteLoading}>
                                キャンセル
                            </button>
                            <button className="btn-delete fus-btn-no-margin" onClick={handleDelete} disabled={deleteLoading}>
                                {deleteLoading ? '処理中...' : '削除する'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {overwriteConfirmVisible && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3 className="modal-title">上書きの確認</h3>
                        <p className="fus-delete-confirm-text">
                            既存のファイルは完全に上書きされますがよろしいですか？
                        </p>
                        <div className="modal-actions">
                            <button className="modal-button-cancel" onClick={() => setOverwriteConfirmVisible(false)} disabled={uploading}>
                                キャンセル
                            </button>
                            <button className="btn-save fus-btn-no-margin" onClick={executeUpload} disabled={uploading}>
                                {uploading ? '処理中...' : '上書きする'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="detail-item fus-container">
                <div className="fus-header">
                    <span className="detail-label">{label}</span>
                    {multiFile && (
                        <span className="fus-meta-info">
                            {attachments.length} / {maxFileCount}件 ({formatFileSize(currentTotalSize)} / {maxTotalSizeMB}MB)
                        </span>
                    )}
                </div>

                {/* 登録済みファイルリスト */}
                {attachments.length > 0 && (
                    <div className="fus-registered-list">
                        {attachments.map(att => {
                            const notice = getAttachmentNotice ? getAttachmentNotice(att) : null;
                            return (
                            <div key={att.id} className="fus-file-card">
                                <div className="fus-file-card-left">
                                    <div className="fus-file-icon-wrapper">
                                        <svg className="fus-file-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
                                            <polyline points="14 2 14 8 20 8" />
                                        </svg>
                                    </div>
                                    <div className="fus-file-meta">
                                        <span className="fus-file-name">{att.file_name}</span>
                                        <span className="fus-file-size">{formatFileSize(att.file_size)}</span>
                                        {notice && (
                                            // 短縮形 (バッジ) だけでは何が起きたか伝わらないため、全文も併記する
                                            // (ツールチップだけにしない。タッチ環境で読めないため。Issue #1057)
                                            //
                                            // live region (role="status") にしない: 注記はファイルに付く
                                            // 持続的な説明であって状態の通知ではなく、一覧は最大 maxFileCount 件
                                            // あるため、取り直しのたびに該当する全件が読み上げられる
                                            <div className="fus-file-notice">
                                                <span className="fus-file-notice-badge">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12" aria-hidden="true">
                                                        <circle cx="12" cy="12" r="10" />
                                                        <line x1="12" y1="8" x2="12" y2="12" />
                                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                                    </svg>
                                                    {notice.badge}
                                                </span>
                                                <span className="fus-file-notice-text">{notice.full}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="fus-file-card-actions">
                                    <button className="fus-action-btn fus-action-view" onClick={() => handleView(att)} title="閲覧">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    </button>
                                    <button className="fus-action-btn fus-action-delete" onClick={() => setDeleteTargetId(att.id || 'single')} title="削除">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                            <polyline points="3 6 5 6 21 6" />
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}

                {/* トグルボタン（ファイルが1件以上あり、かつ枠を隠している場合） */}
                {attachments.length > 0 && (!showDropzone || replaceBlocked) && (
                    <button
                        className="fus-replace-toggle"
                        onClick={() => setShowDropzone(true)}
                        style={{ marginTop: '4px' }}
                        disabled={replaceBlocked}
                        aria-describedby={replaceBlocked ? replaceReasonId : undefined}
                    >
                        {multiFile ? "▶︎ ファイルを追加する" : "▶︎ 上書きする"}
                    </button>
                )}
                {replaceBlocked && (
                    <p id={replaceReasonId} className="fus-replace-disabled-reason">
                        {replaceDisabledReason}
                    </p>
                )}

                {/* 上限到達時のメッセージ枠（トグルを開いた時のみ表示） */}
                {(isAtLimit && showDropzone) && (
                    <div className="fus-upload-area" style={{ marginTop: '12px' }}>
                        <div className="fus-dropzone fus-dropzone-disabled">
                            <div className="fus-dropzone-prompt">
                                <svg className="fus-dropzone-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="8" x2="12" y2="12"></line>
                                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                </svg>
                                <span className="fus-dropzone-main-text" style={{ color: '#6b7280' }}>
                                    最大ファイル数（{maxFileCount}件）に達しています
                                </span>
                                <span className="fus-dropzone-sub-text" style={{ color: '#9ca3af' }}>
                                    新しいファイルを追加するには、先に登録済みのファイルを削除してください
                                </span>
                            </div>
                        </div>
                        <button 
                            className="fus-replace-toggle" 
                            onClick={() => setShowDropzone(false)}
                            style={{ marginTop: '8px', color: '#9ca3af' }}
                        >
                            キャンセル（閉じる）
                        </button>
                    </div>
                )}

                {/* ドロップゾーン (ファイル0件、またはトグル展開時に表示) */}
                {(!isAtLimit && !replaceBlocked && (attachments.length === 0 || showDropzone)) && (
                    <div className="fus-upload-area" style={{ marginTop: '12px' }}>
                        <div
                            className={`fus-dropzone ${isDragActive ? 'fus-drag-active' : ''}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input ref={fileInputRef} type="file" multiple={multiFile} className="fus-hidden-input" onChange={handleFileChange} accept={accept} disabled={uploading} />
                            
                            <div className="fus-dropzone-prompt">
                                <svg className="fus-dropzone-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <polyline points="17 8 12 3 7 8" />
                                    <line x1="12" y1="3" x2="12" y2="15" />
                                </svg>
                                <span className="fus-dropzone-main-text">
                                    {(!multiFile && attachments.length > 0) ? "ファイルを選択して上書きする" : "クリックまたはファイルをドラッグ＆ドロップ"}
                                </span>
                                <span className="fus-dropzone-sub-text">
                                    {multiFile ? `最大 ${maxFileCount}件 (合計 ${maxTotalSizeMB}MB)` : `最大 ${maxFileSizeMB}MB`} のファイルを追加できます
                                </span>
                            </div>
                        </div>

                        {/* トグルを閉じる（キャンセル）ボタン：既にファイルがある場合のみ表示 */}
                        {attachments.length > 0 && selectedFiles.length === 0 && (
                            <button 
                                className="fus-replace-toggle" 
                                onClick={() => setShowDropzone(false)}
                                style={{ marginTop: '8px', color: '#9ca3af' }}
                            >
                                キャンセル（閉じる）
                            </button>
                        )}

                        {/* 選択中のファイル一覧 */}
                        {selectedFiles.length > 0 && (
                            <div className="fus-selected-list">
                                {selectedFiles.map((f, idx) => (
                                    <div key={idx} className="fus-selected-item">
                                        <span className="fus-selected-name">{f.name}</span>
                                        <span className="fus-selected-size">{formatFileSize(f.size)}</span>
                                        <button className="fus-cancel-btn" onClick={() => removeSelectedFile(idx)}>✕</button>
                                    </div>
                                ))}
                                <button className="fus-upload-btn" onClick={handleUpload} disabled={uploading}>
                                    {(!multiFile && attachments.length > 0) ? "上書きアップロード" : "アップロード"} (合計 {formatFileSize(selectedTotalSize)})
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {error && <p className="upload-error-message">{error}</p>}
            </div>
        </>
    );
}

export default FileUploadSection;
